/**
 * A trade request asks to meet a person. It is not about a card.
 *
 * Tapping a tradable card is a door into that person's trade binder. The card does not
 * travel with the request and is never its subject: two people have one request between
 * them however many of each other's cards they tapped.
 *
 * What the app deliberately does not say is "you are meeting because of your trade
 * binders". That is something people work out - they look through a binder, they want
 * what is in it, they ask to meet. Saying it out loud turns a discovery into a rule.
 * So the confirmation offers the binder and explains nothing.
 */
import { supabase } from '../lib/supabase';

/** Headline of the confirmation. The person, not the card. */
export function confirmTitle(username) {
  return `Start a chat with ${username || 'this trader'}?`;
}

/** The one line under it. What happens next, not why. */
export const CONFIRM_BODY = 'Chase will help you agree on a place and time to meet.';

export const CONFIRM_LOOK = 'Look through their Trade Binder';
export const CONFIRM_GO = 'Start the chat';

/** Shown once a request is open with someone, on every card of theirs. */
export const ALREADY_ASKED = 'Chat requested';
export const ALREADY_CHATTING = 'You have a trade in progress';

/**
 * Whether these two already have something going, so every card of theirs shows the
 * same state rather than only the one that was tapped.
 *
 * @returns {Promise<{status: string, id: string}|null>} the open request, if any.
 */
export async function openRequestWith(currentUserId, otherUserId) {
  if (!currentUserId || !otherUserId || currentUserId === otherUserId) return null;
  const { data, error } = await supabase
    .from('trade_requests')
    .select('id, status, requester_id, card_owner_id')
    .or(
      `and(requester_id.eq.${currentUserId},card_owner_id.eq.${otherUserId}),`
      + `and(requester_id.eq.${otherUserId},card_owner_id.eq.${currentUserId})`,
    )
    .in('status', ['pending', 'accepted'])
    .order('created_at', { ascending: false })
    .limit(1);
  if (error) {
    console.error('[tradeRequest] openRequestWith failed:', error);
    return null;
  }
  return data?.[0] || null;
}

/**
 * Ask to meet someone.
 *
 * `fromCardId` is provenance only - where they came in from - and the database comment
 * on that column says as much. It is never read back as what was wanted.
 *
 * A second ask while one is open is not an error worth showing: the unique index says
 * one open request per pair, so the existing one is simply returned.
 */
export async function requestChat({ requesterId, ownerId, fromCardId = null }) {
  if (!requesterId || !ownerId) return { error: new Error('Missing profile') };

  const existing = await openRequestWith(requesterId, ownerId);
  if (existing) return { data: existing, alreadyOpen: true };

  const { data, error } = await supabase
    .from('trade_requests')
    .insert({
      requester_id: requesterId,
      card_owner_id: ownerId,
      card_id: fromCardId,
      status: 'pending',
      viewed_by_owner: false,
    })
    .select('id, status, requester_id, card_owner_id')
    .single();

  // Someone tapped twice, or two cards at once. The index caught it; the answer is
  // still "you have asked them", not an error.
  if (error?.code === '23505') {
    const open = await openRequestWith(requesterId, ownerId);
    if (open) return { data: open, alreadyOpen: true };
  }
  if (error) return { error };
  return { data };
}

/** Take back a request that has not been answered. */
export async function cancelRequest(requestId) {
  const { error } = await supabase
    .from('trade_requests')
    .delete()
    .eq('id', requestId)
    .eq('status', 'pending');
  return { error };
}
